/**
 * Assign-on-behalf-of chip labels and preferred assigner resolution.
 * Mirrors backend {@see ResolvePotentialAssigners}.
 */

export function firstNameFromAssignerOption(option) {
    if (!option) {
        return "";
    }
    if (option.first_name?.trim()) {
        return option.first_name.trim();
    }
    const full = option.full_name?.trim() ?? "";
    if (!full) {
        return "";
    }
    return full.split(/\s+/)[0] ?? full;
}

export function getPreferredPotentialAssigner(potentialAssigners = []) {
    if (!potentialAssigners?.length) {
        return null;
    }
    return (
        potentialAssigners.find((option) => option.is_preferred) ??
        potentialAssigners[0]
    );
}

export function getPreferredAssignerProfileId(potentialAssigners = []) {
    const preferred = getPreferredPotentialAssigner(potentialAssigners);
    return preferred?.user_profile_id ?? null;
}

/** True when the chip should read "Assign" (actor is the sole assigner). */
export function isPlainAssignLabel(
    potentialAssigners = [],
    currentUserProfileId,
    assigneeIsOrganizationOwner = false,
) {
    if (!potentialAssigners?.length) {
        return true;
    }
    if (potentialAssigners.length !== 1) {
        return false;
    }

    const soleAssignerIsActor =
        Number(potentialAssigners[0].user_profile_id) ===
        Number(currentUserProfileId);

    if (!soleAssignerIsActor) {
        return false;
    }

    // Org owner self-assign always shows explicit assign-as UI.
    if (assigneeIsOrganizationOwner) {
        return false;
    }

    return true;
}

export function hasAssignOnBehalfOptions(
    potentialAssigners = [],
    currentUserProfileId = null,
    assigneeIsOrganizationOwner = false,
) {
    if (!potentialAssigners?.length) {
        return false;
    }

    if (
        currentUserProfileId != null &&
        isPlainAssignLabel(
            potentialAssigners,
            currentUserProfileId,
            assigneeIsOrganizationOwner,
        )
    ) {
        return false;
    }

    return true;
}

export function hasAssignOnBehalfDropdown(potentialAssigners = []) {
    return (potentialAssigners?.length ?? 0) > 1;
}

/** Plain assign flow: no options, or sole option is the current user (except org-owner self-assign). */
export function isPlainAssignFlow(
    potentialAssigners = [],
    currentUserProfileId,
    assigneeIsOrganizationOwner = false,
) {
    return isPlainAssignLabel(
        potentialAssigners,
        currentUserProfileId,
        assigneeIsOrganizationOwner,
    );
}

export function getAssignedChipLabel({
    potentialAssigners = [],
    selectedAssignerProfileId = null,
    currentUserProfileId = null,
    assigneeIsOrganizationOwner = false,
}) {
    if (
        isPlainAssignFlow(
            potentialAssigners,
            currentUserProfileId,
            assigneeIsOrganizationOwner,
        )
    ) {
        return "Assigned";
    }

    return "Assigned as";
}

export function getAssignChipLabel({
    potentialAssigners = [],
    currentUserProfileId = null,
    isAssigned = false,
    assigneeIsOrganizationOwner = false,
}) {
    if (isAssigned) {
        return "Assigned";
    }

    if (
        isPlainAssignLabel(
            potentialAssigners,
            currentUserProfileId,
            assigneeIsOrganizationOwner,
        )
    ) {
        return "Assign";
    }

    if (
        hasAssignOnBehalfOptions(
            potentialAssigners,
            currentUserProfileId,
            assigneeIsOrganizationOwner,
        )
    ) {
        return "Assign as";
    }

    return "Assign";
}

export function findPotentialAssignerOption(
    potentialAssigners = [],
    profileId,
) {
    if (profileId == null) {
        return null;
    }

    return (
        (potentialAssigners ?? []).find(
            (option) => Number(option.user_profile_id) === Number(profileId),
        ) ?? null
    );
}

export function getAssignDropdownOptionLabel(
    option,
    { assigned = false, selectedAssignerProfileId = null } = {},
) {
    if (
        assigned &&
        selectedAssignerProfileId != null &&
        Number(option?.user_profile_id) === Number(selectedAssignerProfileId)
    ) {
        return "Assigned as";
    }

    return "Assign as";
}

export function getAssignDropdownOptions(
    potentialAssigners = [],
    currentUserProfileId,
    { assigned = false, selectedAssignerProfileId = null } = {},
) {
    return (potentialAssigners ?? []).map((option) => ({
        user_profile_id: option.user_profile_id,
        full_name: option.full_name ?? "",
        first_name: option.first_name ?? "",
        avatar_url: option.avatar_url ?? null,
        label: getAssignDropdownOptionLabel(option, {
            assigned,
            selectedAssignerProfileId,
        }),
        is_preferred: Boolean(option.is_preferred),
        is_actor:
            Number(option.user_profile_id) === Number(currentUserProfileId),
    }));
}
